import { AyumiaError } from '../errors.js';
import { add as addDecimal, decimal, toText as decimalText } from '../pricing/decimal.js';
import { canonicallyEqual } from '../model/canonical-json.js';
import { gs1CheckDigitValid } from './gs1.js';
import { qualifiedKey, qualifierIssues } from '../identifiers/qualified-key.js';
import { isObjectId } from '../object-id.js';
/** Arité des opérateurs de 58 §8 : `[min, max]`, `Infinity` pour « au moins min ». */
const OPERATOR_ARITY = {
    $and: [1, Infinity],
    $or: [1, Infinity],
    $not: [1, 1],
    $eq: [2, 2],
    $ne: [2, 2],
    $gt: [2, 2],
    $gte: [2, 2],
    $lt: [2, 2],
    $lte: [2, 2],
    $in: [2, 2],
    $nin: [2, 2],
    $exists: [1, 1],
    $isNull: [1, 1],
    $isEmpty: [1, 1],
    $add: [2, Infinity],
    $subtract: [2, 2],
    $multiply: [2, Infinity],
    $divide: [2, 2],
    $round: [1, 2],
    $concat: [1, Infinity],
    $contains: [2, 2],
    $startsWith: [2, 2],
    $endsWith: [2, 2],
    $if: [3, 3],
};
export const EXPRESSION_OPERATORS = [...Object.keys(OPERATOR_ARITY), '$switch', '$literal', '$fn'];
const REFERENCE = /^\$([a-z][a-z0-9_]*)((?:\.[a-z0-9_]+)*)$/;
const FUNCTION_NAME = /^[a-z][a-z0-9_]*(\.[a-z][a-z0-9_]*)+$/;
const FORBIDDEN_SEGMENTS = new Set(['__proto__', 'prototype', 'constructor']);
export const MAX_EXPRESSION_DEPTH = 32;
export const MAX_EXPRESSION_NODES = 1000;
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Date) && !isObjectId(value);
}
// ---------------------------------------------------------------------------------------------
// Registre de fonctions gouverné
// ---------------------------------------------------------------------------------------------
function toDate(value) {
    const date = value instanceof Date ? value : typeof value === 'string' ? new Date(value) : null;
    if (date === null || Number.isNaN(date.getTime()))
        throw evaluationError(`« ${String(value)} » n'est pas une date.`);
    return date;
}
function toNumber(value) {
    if (typeof value !== 'number' || !Number.isFinite(value))
        throw evaluationError(`« ${String(value)} » n'est pas un nombre.`);
    return value;
}
function toText(value) {
    if (typeof value !== 'string')
        throw evaluationError(`« ${String(value)} » n'est pas un texte.`);
    return value;
}
const DAY_MS = 24 * 60 * 60 * 1000;
/**
 * Registre des fonctions appelables par `$fn`. Il ne contient que des fonctions pures ; une
 * fonction absente fait échouer la validation, pas l'exécution. Un Pack n'y ajoute rien sans ADR.
 */
export class FunctionRegistry {
    functions = new Map();
    register(name, definition) {
        if (!FUNCTION_NAME.test(name))
            throw new Error(`« ${name} » n'est pas un nom de fonction (domaine.nom).`);
        if (this.functions.has(name))
            throw new Error(`La fonction ${name} est déjà enregistrée.`);
        this.functions.set(name, definition);
        return this;
    }
    get(name) {
        return this.functions.get(name);
    }
    names() {
        return [...this.functions.keys()].sort();
    }
    /** Fonctions de base : texte, nombres, listes, dates — aucune ne lit l'horloge. */
    static standard() {
        return new FunctionRegistry()
            .register('string.lower', { arity: [1, 1], apply: ([value]) => toText(value).toLowerCase() })
            .register('string.upper', { arity: [1, 1], apply: ([value]) => toText(value).toUpperCase() })
            .register('string.trim', { arity: [1, 1], apply: ([value]) => toText(value).trim() })
            .register('string.length', { arity: [1, 1], apply: ([value]) => toText(value).length })
            .register('number.abs', { arity: [1, 1], apply: ([value]) => Math.abs(toNumber(value)) })
            .register('number.min', { arity: [1, Infinity], apply: (args) => Math.min(...args.map(toNumber)) })
            .register('number.max', { arity: [1, Infinity], apply: (args) => Math.max(...args.map(toNumber)) })
            .register('array.length', {
            arity: [1, 1],
            apply: ([value]) => {
                if (!Array.isArray(value))
                    throw evaluationError('array.length attend une liste.');
                return value.length;
            },
        })
            // Valeurs d'un champ sur une liste d'objets (rôles d'un tiers, ADR-082) : pour `$in`, `$contains`.
            .register('array.pluck', {
            arity: [2, 2],
            apply: ([list, path]) => {
                if (list === null || list === undefined)
                    return [];
                if (!Array.isArray(list))
                    throw evaluationError('array.pluck attend une liste.');
                const segments = toText(path).split('.');
                return list.map((entry) => {
                    let value = entry;
                    for (const segment of segments)
                        value = isPlainObject(value) ? value[segment] : undefined;
                    return value ?? null;
                });
            },
        })
            // Somme exacte d'un champ sur une liste (pied de tableau d'un document, ADR-060) : décimal, jamais flottant.
            .register('array.sum', {
            arity: [1, 2],
            apply: ([list, path]) => {
                if (!Array.isArray(list))
                    throw evaluationError('array.sum attend une liste.');
                const segments = path === undefined || path === null ? [] : toText(path).split('.');
                let total = decimal(0);
                for (const entry of list) {
                    let value = entry;
                    for (const segment of segments)
                        value = isPlainObject(value) ? value[segment] : undefined;
                    if (value === undefined || value === null || value === '')
                        continue;
                    if (typeof value !== 'number' && !(typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)))
                        throw evaluationError(`array.sum : « ${String(value)} » n'est pas un nombre.`);
                    total = addDecimal(total, decimal(value));
                }
                return Number(decimalText(total));
            },
        })
            .register('date.add_days', { arity: [2, 2], apply: ([date, days]) => new Date(toDate(date).getTime() + toNumber(days) * DAY_MS) })
            // Année civile UTC d'une date : l'année d'une bague de chasse (ADR-061 §5).
            .register('date.year', { arity: [1, 1], apply: ([date]) => toDate(date).getUTCFullYear() })
            .register('date.diff_days', { arity: [2, 2], apply: ([a, b]) => (toDate(a).getTime() - toDate(b).getTime()) / DAY_MS })
            // Vérifie un identifiant attribué par GS1, n'en fabrique aucun (ADR-053).
            .register('gs1.check_digit_valid', { arity: [2, 2], apply: ([value, type]) => gs1CheckDigitValid(toText(value), toText(type)) })
            // Identifiant qualifié (ADR-020 §1) : clé normalisée et qualifiants admis par le type.
            .register('identifier.qualified_key', { arity: [1, 2], apply: ([value, qualifiers]) => qualifiedKey(toText(value), qualifiers) })
            .register('identifier.qualifiers_valid', { arity: [2, 2], apply: ([type, qualifiers]) => qualifierIssues(toText(type), qualifiers).length === 0 });
    }
}
/** Valide une expression sans l'évaluer. Liste vide = expression valide. */
export function validateExpression(expression, options = {}) {
    const findings = [];
    const functions = options.functions ?? FunctionRegistry.standard();
    let nodes = 0;
    const add = (path, code, message) => {
        findings.push({ path, code, message });
    };
    const visit = (node, path, depth) => {
        nodes += 1;
        if (depth > MAX_EXPRESSION_DEPTH) {
            add(path, 'EXPRESSION_TOO_DEEP', `Profondeur maximale : ${MAX_EXPRESSION_DEPTH}.`);
            return;
        }
        if (nodes > MAX_EXPRESSION_NODES) {
            if (nodes === MAX_EXPRESSION_NODES + 1)
                add(path, 'EXPRESSION_TOO_LARGE', `Au plus ${MAX_EXPRESSION_NODES} nœuds.`);
            return;
        }
        if (node === null || typeof node === 'number' || typeof node === 'boolean') {
            if (typeof node === 'number' && !Number.isFinite(node))
                add(path, 'EXPRESSION_NUMBER_INVALID', 'Nombre non fini.');
            return;
        }
        if (typeof node === 'string') {
            if (!node.startsWith('$'))
                return;
            const match = REFERENCE.exec(node);
            if (match === null) {
                add(path, 'EXPRESSION_REFERENCE_INVALID', `« ${node} » n'est pas une référence ($racine.chemin) ; un texte commençant par $ s'écrit {"$literal": …}.`);
                return;
            }
            const segments = [match[1], ...match[2].split('.').filter((segment) => segment !== '')];
            if (segments.some((segment) => FORBIDDEN_SEGMENTS.has(segment)))
                add(path, 'EXPRESSION_REFERENCE_FORBIDDEN', `Segment interdit dans « ${node} ».`);
            if (options.roots !== undefined && !options.roots.includes(match[1])) {
                add(path, 'EXPRESSION_ROOT_UNKNOWN', `La racine « ${match[1]} » n'est pas dans la portée (${options.roots.join(', ')}).`);
            }
            return;
        }
        if (Array.isArray(node)) {
            node.forEach((entry, index) => visit(entry, `${path}[${index}]`, depth + 1));
            return;
        }
        if (!isPlainObject(node)) {
            add(path, 'EXPRESSION_VALUE_INVALID', 'Valeur non représentable dans une expression.');
            return;
        }
        const keys = Object.keys(node);
        if (keys.length !== 1) {
            add(path, 'EXPRESSION_OBJECT_INVALID', 'Un objet d’expression a une seule clé : un opérateur, $literal, $switch ou $fn.');
            return;
        }
        const operator = keys[0];
        const argument = node[operator];
        if (operator === '$literal')
            return;
        if (operator === '$switch') {
            if (!isPlainObject(argument) || !Array.isArray(argument['branches']) || argument['branches'].length === 0) {
                add(path, 'EXPRESSION_SWITCH_INVALID', '$switch attend { branches: [{ case, then }], default }.');
                return;
            }
            argument['branches'].forEach((branch, index) => {
                if (!isPlainObject(branch) || !('case' in branch) || !('then' in branch) || Object.keys(branch).length !== 2) {
                    add(`${path}.$switch.branches[${index}]`, 'EXPRESSION_SWITCH_INVALID', 'Chaque branche est { case, then }.');
                    return;
                }
                visit(branch['case'], `${path}.$switch.branches[${index}].case`, depth + 1);
                visit(branch['then'], `${path}.$switch.branches[${index}].then`, depth + 1);
            });
            if ('default' in argument)
                visit(argument['default'], `${path}.$switch.default`, depth + 1);
            return;
        }
        if (operator === '$fn') {
            if (!isPlainObject(argument) || typeof argument['name'] !== 'string' || !Array.isArray(argument['args'])) {
                add(path, 'EXPRESSION_FUNCTION_INVALID', '$fn attend { name, args }.');
                return;
            }
            const fn = functions.get(argument['name']);
            const args = argument['args'];
            if (fn === undefined)
                add(path, 'EXPRESSION_FUNCTION_UNKNOWN', `La fonction « ${argument['name']} » n'est pas au registre.`);
            else if (args.length < fn.arity[0] || args.length > fn.arity[1])
                add(path, 'EXPRESSION_ARITY', `${argument['name']} attend ${fn.arity[0]} à ${fn.arity[1]} arguments.`);
            args.forEach((entry, index) => visit(entry, `${path}.$fn.args[${index}]`, depth + 1));
            return;
        }
        const arity = OPERATOR_ARITY[operator];
        if (arity === undefined) {
            add(path, 'EXPRESSION_OPERATOR_UNKNOWN', `« ${operator} » n'est pas un opérateur (58 §8).`);
            return;
        }
        if (!Array.isArray(argument)) {
            add(path, 'EXPRESSION_ARGUMENTS_INVALID', `${operator} attend un tableau d'arguments.`);
            return;
        }
        if (argument.length < arity[0] || argument.length > arity[1]) {
            add(path, 'EXPRESSION_ARITY', `${operator} attend ${arity[0]}${arity[1] === arity[0] ? '' : arity[1] === Infinity ? ' ou plus' : ` à ${arity[1]}`} arguments, pas ${argument.length}.`);
        }
        argument.forEach((entry, index) => visit(entry, `${path}.${operator}[${index}]`, depth + 1));
    };
    visit(expression, '$', 0);
    return findings;
}
/** Comme {@link validateExpression}, mais lève à la première erreur. */
export function assertValidExpression(expression, options = {}) {
    const findings = validateExpression(expression, options);
    if (findings.length > 0) {
        throw new AyumiaError('VALIDATION_ERROR', 'EXPRESSION_INVALID', 'Expression invalide.', {
            fieldErrors: findings.map((finding) => ({ field: finding.path, code: finding.code, message: finding.message })),
        });
    }
}
// ---------------------------------------------------------------------------------------------
// Évaluation
// ---------------------------------------------------------------------------------------------
function evaluationError(message) {
    return new AyumiaError('EXECUTION_ERROR', 'EXPRESSION_EVALUATION_FAILED', message);
}
/** Vérité au sens MongoDB : `false`, `null`, absent et `0` sont faux ; tout le reste est vrai. */
export function isTruthy(value) {
    return !(value === false || value === null || value === undefined || value === 0);
}
function comparable(value) {
    if (typeof value === 'number' || typeof value === 'string')
        return value;
    if (value instanceof Date)
        return value.getTime();
    return null;
}
/** Comparaison ordonnée : même nature ou rien. Une comparaison entre natures différentes est fausse. */
function compare(a, b, test) {
    const left = comparable(a);
    const right = comparable(b);
    if (left === null || right === null || typeof left !== typeof right)
        return false;
    return test(left < right ? -1 : left > right ? 1 : 0);
}
function resolveReference(reference, scope) {
    const match = REFERENCE.exec(reference);
    if (match === null)
        throw evaluationError(`Référence invalide : « ${reference} ».`);
    const segments = [match[1], ...match[2].split('.').filter((segment) => segment !== '')];
    let current = scope;
    for (const segment of segments) {
        if (FORBIDDEN_SEGMENTS.has(segment))
            throw evaluationError(`Segment interdit : « ${segment} ».`);
        if (Array.isArray(current) && /^\d+$/.test(segment)) {
            current = current[Number(segment)];
        }
        else if (isPlainObject(current) && Object.prototype.hasOwnProperty.call(current, segment)) {
            current = current[segment];
        }
        else {
            return undefined;
        }
    }
    return current;
}
function isEmpty(value) {
    if (value === null || value === undefined || value === '')
        return true;
    if (Array.isArray(value))
        return value.length === 0;
    if (isPlainObject(value))
        return Object.keys(value).length === 0;
    return false;
}
/**
 * Évalue une expression sur une portée. Une référence absente vaut `null`. Une expression mal
 * formée ou une opération impossible (division par zéro, texte attendu) lève une erreur
 * d'exécution non retentable : réessayer ne la corrigera pas.
 */
/**
 * Corrige le bruit binaire d'une opération (74,6 − 2,8 − 0,4 = 71,39999999999999) : quinze chiffres
 * significatifs, bien au-delà de toute mesure. Ce n'est pas un arrondi métier — celui-ci reste
 * `$round`, ou l'arithmétique décimale des montants (ADR-056).
 */
function exact(value) {
    return Number.isFinite(value) ? Number(value.toPrecision(15)) : value;
}
export function evaluateExpression(expression, scope, options = {}) {
    const functions = options.functions ?? FunctionRegistry.standard();
    let nodes = 0;
    const run = (node, depth) => {
        nodes += 1;
        if (depth > MAX_EXPRESSION_DEPTH)
            throw evaluationError(`Profondeur maximale dépassée (${MAX_EXPRESSION_DEPTH}).`);
        if (nodes > MAX_EXPRESSION_NODES)
            throw evaluationError(`Trop de nœuds évalués (${MAX_EXPRESSION_NODES}).`);
        if (node === null || typeof node === 'number' || typeof node === 'boolean')
            return node;
        if (typeof node === 'string')
            return node.startsWith('$') ? (resolveReference(node, scope) ?? null) : node;
        if (Array.isArray(node))
            return node.map((entry) => run(entry, depth + 1));
        if (!isPlainObject(node))
            throw evaluationError('Valeur non représentable dans une expression.');
        const keys = Object.keys(node);
        if (keys.length !== 1)
            throw evaluationError('Un objet d’expression a une seule clé.');
        const operator = keys[0];
        const argument = node[operator];
        if (operator === '$literal')
            return argument;
        if (operator === '$switch') {
            const spec = argument;
            for (const branch of spec.branches)
                if (isTruthy(run(branch.case, depth + 1)))
                    return run(branch.then, depth + 1);
            return 'default' in spec ? run(spec.default, depth + 1) : null;
        }
        if (operator === '$fn') {
            const call = argument;
            const fn = functions.get(call.name);
            if (fn === undefined)
                throw evaluationError(`Fonction inconnue : « ${call.name} ».`);
            return fn.apply(call.args.map((entry) => run(entry, depth + 1)));
        }
        if (!Array.isArray(argument) || OPERATOR_ARITY[operator] === undefined)
            throw evaluationError(`Opérateur inconnu ou mal formé : « ${operator} ».`);
        const args = argument;
        const arg = (index) => run(args[index], depth + 1);
        const all = () => args.map((entry) => run(entry, depth + 1));
        switch (operator) {
            // Évaluation paresseuse : un $and s'arrête au premier faux, un $or au premier vrai.
            case '$and':
                return args.every((entry) => isTruthy(run(entry, depth + 1)));
            case '$or':
                return args.some((entry) => isTruthy(run(entry, depth + 1)));
            case '$not':
                return !isTruthy(arg(0));
            case '$if':
                return isTruthy(arg(0)) ? arg(1) : arg(2);
            case '$eq':
                return canonicallyEqual(arg(0) ?? null, arg(1) ?? null);
            case '$ne':
                return !canonicallyEqual(arg(0) ?? null, arg(1) ?? null);
            case '$gt':
                return compare(arg(0), arg(1), (order) => order > 0);
            case '$gte':
                return compare(arg(0), arg(1), (order) => order >= 0);
            case '$lt':
                return compare(arg(0), arg(1), (order) => order < 0);
            case '$lte':
                return compare(arg(0), arg(1), (order) => order <= 0);
            case '$in':
            case '$nin': {
                const value = arg(0);
                const list = arg(1);
                if (!Array.isArray(list))
                    throw evaluationError(`${operator} attend une liste en second argument.`);
                const found = list.some((candidate) => canonicallyEqual(candidate ?? null, value ?? null));
                return operator === '$in' ? found : !found;
            }
            case '$exists': {
                const target = args[0];
                if (typeof target === 'string' && target.startsWith('$'))
                    return resolveReference(target, scope) !== undefined;
                return arg(0) !== null;
            }
            case '$isNull':
                return arg(0) === null;
            case '$isEmpty':
                return isEmpty(arg(0));
            case '$add':
                return exact(all().map(toNumber).reduce((sum, value) => sum + value, 0));
            case '$multiply':
                return exact(all().map(toNumber).reduce((product, value) => product * value, 1));
            case '$subtract':
                return exact(toNumber(arg(0)) - toNumber(arg(1)));
            case '$divide': {
                const divisor = toNumber(arg(1));
                if (divisor === 0)
                    throw evaluationError('Division par zéro.');
                return exact(toNumber(arg(0)) / divisor);
            }
            case '$round': {
                const places = args.length > 1 ? toNumber(arg(1)) : 0;
                if (!Number.isInteger(places) || places < 0 || places > 12)
                    throw evaluationError('$round : nombre de décimales entre 0 et 12.');
                const factor = 10 ** places;
                return Math.round(toNumber(arg(0)) * factor) / factor;
            }
            case '$concat':
                return all().map(toText).join('');
            case '$contains': {
                const container = arg(0);
                const value = arg(1);
                if (typeof container === 'string')
                    return container.includes(toText(value));
                if (Array.isArray(container))
                    return container.some((candidate) => canonicallyEqual(candidate ?? null, value ?? null));
                return false;
            }
            case '$startsWith':
                return toText(arg(0)).startsWith(toText(arg(1)));
            case '$endsWith':
                return toText(arg(0)).endsWith(toText(arg(1)));
            default:
                throw evaluationError(`Opérateur non pris en charge : « ${operator} ».`);
        }
    };
    return run(expression, 0);
}
//# sourceMappingURL=expressions.js.map